import classes from "./StudioCarousel.module.css"
import { useState, useEffect, useCallback, useRef, useMemo } from "react";

function CarouselSlideImage({ attemptUrls, alt, className }) {
  const [currentSrc, setCurrentSrc] = useState(() => attemptUrls[0] || "");
  const loadTierRef = useRef(0);
  const attemptUrlsRef = useRef(attemptUrls);
  attemptUrlsRef.current = attemptUrls;
  const key = attemptUrls.join("\u0001");

  useEffect(() => {
    setCurrentSrc(attemptUrls[0] || "");
    loadTierRef.current = 0;
  }, [key]);

  const onError = () => {
    const urls = attemptUrlsRef.current;
    const t = loadTierRef.current;
    const next = t + 1;
    if (next < urls.length) {
      loadTierRef.current = next;
      const u = urls[next];
      const sep = u.includes("?") ? "&" : "?";
      setCurrentSrc(`${u}${sep}_car=${Date.now()}`);
    }
  };

  if (!currentSrc) return null;

  return (
    <img
      key={currentSrc}
      src={currentSrc}
      alt={alt}
      className={className}
      onError={onError}
      decoding="async"
    />
  );
}

export const StudioCarousel = ({ slideAttempts, images, passthroughImages, fallbackImages, startIndex, onClose }) => {
  const resolvedAttempts = useMemo(() => {
    if (slideAttempts?.length) return slideAttempts;
    if (!images?.length) return [];
    return images.map((src, i) => {
      const row = [src];
      if (passthroughImages?.[i]) row.push(passthroughImages[i]);
      if (fallbackImages?.[i]) row.push(fallbackImages[i]);
      return [...new Set(row)].filter(Boolean);
    });
  }, [slideAttempts, images, passthroughImages, fallbackImages]);

  const slideCount = resolvedAttempts.length;

  const [currentIndex, setCurrentIndex] = useState(startIndex);

  useEffect(() => {
    const max = Math.max(0, slideCount - 1);
    const idx = Math.min(Math.max(0, startIndex), max);
    setCurrentIndex(idx);
  }, [startIndex, slideCount]);
  
  const [isFadingOut, setIsFadingOut] = useState(false);
  
  // Enhanced drag/swipe state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  const containerRef = useRef(null);

  const nextImage = useCallback(() => {
    if (!slideCount) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % slideCount);
  }, [slideCount]);

  const prevImage = useCallback(() => {
    if (!slideCount) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + slideCount) % slideCount);
  }, [slideCount]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'ArrowLeft') {
      prevImage();
    } else if (event.key === 'ArrowRight') {
      nextImage();
    }
  }, [nextImage, prevImage]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Enhanced mouse drag handlers
  const handleMouseDown = (e) => {
    if (!slideCount) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragOffset(0);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !slideCount) return;
    const currentX = e.clientX;
    const diff = currentX - dragStartX;
    const container = containerRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    
    // Hard block edge drag beyond bounds
    let constrained = offsetPercent;
    if (currentIndex === 0 && offsetPercent > 0) constrained = 0; // block right swipe on first image
    if (currentIndex === slideCount - 1 && offsetPercent < 0) constrained = 0; // block left swipe on last image
    
    setDragOffset(constrained);
  };

  const commitDrag = () => {
    if (!isDragging || !slideCount) return;
    const threshold = 5; // percent
    const dragDistance = Math.abs(dragOffset);
    if (dragDistance > threshold) {
      if (dragOffset > 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (dragOffset < 0 && currentIndex < slideCount - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }
    setIsDragging(false);
    setDragOffset(0);
  };

  const handleMouseUp = () => commitDrag();
  const handleMouseLeave = () => { if (isDragging) commitDrag(); };

  // Enhanced touch handlers
  const handleTouchStart = (e) => {
    if (!slideCount) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStartX(touch.clientX);
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !slideCount) return;
    // Prevent vertical page scroll while swiping carousel
    if (e.cancelable) e.preventDefault();
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const diff = currentX - dragStartX;
    const container = containerRef.current;
    const containerWidth = container ? container.offsetWidth : 1;
    const offsetPercent = (diff / containerWidth) * 100;
    
    // Hard block edge drag beyond bounds
    let constrained = offsetPercent;
    if (currentIndex === 0 && offsetPercent > 0) constrained = 0; // block right swipe on first image
    if (currentIndex === slideCount - 1 && offsetPercent < 0) constrained = 0; // block left swipe on last image
    
    setDragOffset(constrained);
  };

  const handleTouchEnd = () => commitDrag();

  const handleClose = useCallback(() => {
    setIsFadingOut(true);
  }, []);

  useEffect(() => {
    if (isFadingOut) {
      const timer = setTimeout(() => {
        onClose();
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [isFadingOut, onClose]);

  return (
    <div className={`${classes.carouselContainer} ${isFadingOut ? classes.fadeOut : ''}`}>
        <header>
            <div onClick={handleClose} className={classes.closeButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
                    <path d="M1 1L18 18.5" stroke="white" strokeWidth="1.70431" strokeMiterlimit="10"/>
                    <path d="M18 1L1 18.5" stroke="white" strokeWidth="1.70431" strokeMiterlimit="10"/>
                </svg>
                <span className="mediumLarge">Schließen</span>
            </div>
            {/* <span className={`${classes.laptopMode} ${classes.centeredTitle}`}>Raum A für Foto-Shoots mit Hintergrund (40m²)</span> */}
            <span className={classes.tabletMode}>{currentIndex + 1}/{slideCount || 1}</span>
            {/* <div className={classes.extrasContainer}>
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="20" viewBox="0 0 15 20" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M7.2583 0L10.9611 3.71119L9.95414 4.70343L7.95893 2.70368V12.5396H6.54092V2.71625L4.54493 4.7119L3.53923 3.71844L7.2583 0ZM2.41092 7.55162C1.86699 7.55162 1.418 7.993 1.418 8.55279V17.0896C1.418 17.6494 1.86699 18.0908 2.41092 18.0908H12.0891C12.633 18.0908 13.082 17.6494 13.082 17.0896V8.55279C13.082 7.993 12.633 7.55162 12.0891 7.55162H9.72052V6.14239H12.0891C13.4275 6.14239 14.5 7.226 14.5 8.55279V17.0896C14.5 18.4164 13.4275 19.5 12.0891 19.5H2.41092C1.07254 19.5 0 18.4164 0 17.0896V8.55279C0 7.226 1.07254 6.14239 2.41092 6.14239H4.77948V7.55162H2.41092Z" fill="white"/>
                    </svg>
                    <span>Teilen</span>
                </div>
                <div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="17" viewBox="0 0 20 17" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M2.32105 3.22796L2.31969 3.22999C1.74451 4.0868 1.70646 5.05679 1.87393 5.92884C1.95647 6.35863 2.08365 6.73029 2.1912 6.99445C2.24443 7.1252 2.29119 7.22557 2.3223 7.2889C2.33614 7.31705 2.34551 7.33529 2.35075 7.34529C2.88265 8.22447 3.35801 8.88959 3.84416 9.46027C4.53318 10.2665 6.24335 12.0186 9.7831 14.4136C13.0873 12.1666 14.9441 10.3266 15.7637 9.4205C16.1011 9.04478 16.6181 8.43061 17.1875 7.40185C17.2635 7.25208 17.5349 6.68233 17.6788 5.92569C17.8485 5.03299 17.8078 4.06252 17.2489 3.22999C16.6077 2.27486 15.4716 1.69385 14.4014 1.71957C13.678 1.74226 12.8985 2.03917 12.0718 2.76285L12.0668 2.76714C11.8241 2.97781 11.6106 3.20935 11.4333 3.45442L11.4307 3.45814L9.79988 5.69863L8.14661 3.47302C7.94518 3.20155 7.73221 2.97226 7.49628 2.76783L7.4925 2.76455C6.66932 2.04645 5.89447 1.74686 5.17775 1.72015H5.07775C4.04335 1.72015 2.94187 2.29989 2.32105 3.22796ZM5.20306 0.220151C6.3468 0.25457 7.45272 0.733308 8.48933 1.63759C8.60017 1.73363 8.70689 1.8334 8.81 1.93753C9.00586 2.13533 9.18871 2.34887 9.3621 2.58255L9.78746 3.15516L10.2065 2.57942C10.3717 2.35099 10.5563 2.13497 10.7567 1.93318C10.8582 1.83092 10.9638 1.73231 11.073 1.63759C12.1096 0.730179 13.2155 0.25457 14.3593 0.220151C15.9662 0.179474 17.5857 1.02743 18.5057 2.39794C20.1997 4.92143 18.585 7.98499 18.5202 8.10791C18.5192 8.10982 18.5183 8.1115 18.5183 8.1115C17.8755 9.27549 17.28 9.98577 16.8893 10.4207C15.9189 11.494 13.7228 13.6498 9.78431 16.2188C5.57485 13.4746 3.53944 11.422 2.69188 10.4301C2.13734 9.77926 1.61116 9.03768 1.04087 8.09273C1.02196 8.06456 -0.682611 4.99814 1.06293 2.39794C1.9546 1.06498 3.52054 0.220151 5.07703 0.220151H5.20306ZM2.34605 7.33752C2.34158 7.32986 2.33561 7.31987 2.32823 7.30801L2.33644 7.32161C2.33965 7.32692 2.34285 7.33223 2.34605 7.33752Z" fill="white"/>
                    </svg>
                    <span>Speichern</span>
                </div>
            </div> */}
        </header>
        <div 
          ref={containerRef} 
          className={`${classes.carouselContent} ${isDragging ? classes.dragging : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ 
            touchAction: 'pan-x', 
            overflow: 'hidden'
          }}
        >
            <button onClick={prevImage} className={classes.navigationButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none">
                    <circle cx="23" cy="23" r="22" strokeWidth="2"/>
                    <line x1="26.5996" y1="14.601" x2="17.601" y2="23.5996" stroke="white" strokeWidth="1.7"/>
                    <line x1="17.6256" y1="22.4215" x2="26.6241" y2="32.1699" stroke="white" strokeWidth="1.7"/>
                </svg>
            </button>
            <div className={classes.mainContainer}>
                <div className={classes.imageContainer}>
                    <div 
                      className={`${classes.imageSlider} ${isDragging ? classes.dragging : ''}`}
                      style={{
                        transform: `translateX(${-100 * currentIndex + dragOffset}%)`,
                        transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                      }}
                    >
                        {resolvedAttempts.map((attemptUrls, index) => (
                          <div key={index} className={classes.imageSlide}>
                            <CarouselSlideImage
                              attemptUrls={attemptUrls}
                              alt={`Studio image ${index + 1}`}
                              className={classes.slideImage}
                            />
                          </div>
                        ))}
                    </div>
                </div>
            </div>
            <button onClick={nextImage} className={classes.navigationButton}>
                <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none">
                    <circle cx="23" cy="23" r="22" transform="rotate(180 23 23)" strokeWidth="2"/>
                    <line x1="19.4004" y1="31.399" x2="28.399" y2="22.4004" stroke="white" strokeWidth="1.7"/>
                    <line x1="28.3744" y1="23.5785" x2="19.3759" y2="13.8301" stroke="white" strokeWidth="1.7"/>
                </svg>
            </button>
        </div>
        <footer className={classes.footer}>
            <span className={classes.laptopMode}>{currentIndex + 1}/{slideCount || 1}</span>
            {/* <span className={classes.tabletMode}>Raum A für Foto-Shoots mit Hintergrund (40m²)</span> */}
        </footer>
    </div>
  );
};

export default StudioCarousel;